import { all, call, put, select, takeEvery } from 'typed-redux-saga';
import { push } from 'connected-react-router';

import { getCoamTenant, getRegionSetting } from 'src/selectors';
import type {
    CreateSceneResult,
    CreateSceneVersionResult,
    GetSceneVariationLinkResult,
    SaveSceneVariationAssetResult,
    SceneVariationScene,
} from 'src/types/services';
import type { TenantType } from 'src/types';
import type { LinkType } from 'src/types/link';

import {
    getSceneName,
    getSceneDescription,
    getTags,
    getSceneId,
    getLatestVersionId,
    getPublishedVersionId,
    getSurfaceProperties,
    getSceneNotes,
    getSurfaces,
    getBodyMeasurementOverride,
} from 'src/selectors/pages/editor';
import {
    getAllCylinderWarps,
    getAllGarmentMarkers,
    getAllGarmentPlacements,
    getAllBackgrounds,
    getAllDocuments,
} from 'src/selectors/layers';
import { createScene as createSceneOnServer, updateScene, createSceneVersion } from 'src/services/sceneClient';
import { uploadSceneXml, generateUploadsUrl } from 'src/services/uploadsClient';
import { createLink, getLinksForAsset } from 'src/services/linkClient';
import { fromState } from 'src/services/converters/cylinderWarp';
import { fromState as garmentMarkersFromState } from 'src/services/converters/garmentMarkers';
import { fromState as garmentPlacementsFromState } from 'src/services/converters/garmentPlacement';
import { fromState as bodyMeasurementOverrideFromState } from 'src/services/converters/bodyMeasurementOverride';
import { logError, logInfo } from 'src/loggingManager';
import currentStateToXml from 'src/util/xmlConverter/toXml';
import { deleteAsset } from 'src/services/assetsClient';
import { apiErrorMessage } from 'src/services/apiError';
import { BackgroundType } from 'src/types/background';
import {
    publishScene,
    publishSceneSuccess,
    publishSceneFailed,
    saveDraft,
    saveDraftSuccess,
    saveDraftFailed,
    duplicateScene,
    duplicateSceneSuccess,
    duplicateSceneFailed,
    saveAndPublishScene,
    saveAndPublishSceneSuccess,
    saveAndPublishSceneFailed,
    createScene,
    createSceneSuccess,
    createSceneFailed,
    createBulkScenes,
    createBulkScenesSuccess,
    createBulkScenesFailed,
    createSceneFinished,
} from './slice';
import {
    saveSceneVariationAsset,
    getSceneVariationLink,
    updateSceneVariation,
    updateLinkVariation,
    mapPurposes,
} from '../SceneVariation/service';
import getSceneVariation from '../SceneVariation/selectors/getSceneVariation';
import getSceneData from '../SceneVariation/selectors/getSceneData';
import {
    cleanSceneConfiguration,
    saveLinkVariationFailed,
    saveVariationFailed,
    setLinks,
    updateSceneIds,
} from '../SceneVariation/slice';
import { CylinderType } from 'src/types/transforms';
import { BackgroundGarmentMarkersType, DocumentGarmentPlacementType } from 'src/types/decorationSpecs';
import { SceneType_InitialVersion, SceneType_NotSaved } from 'src/types/scene';

type SaveResult = {
    sceneId: string;
    publishedVersionId: string;
    latestVersionId: string;
};

// Used by all saving functionality to get and build the scene data from the state tree.
function* getScene(background?: BackgroundType): Generator<any, SceneType_NotSaved, any> {
    const { sku, skuVersion, variables, merchantSku, merchantVersion, merchantVariables } =
        yield* select(getSurfaceProperties);
    const name = yield* select(getSceneName);
    const description = yield* select(getSceneDescription);
    const tags = yield* select(getTags);
    const notes = yield* select(getSceneNotes);
    try {
        const state = yield* select();
        const xml = currentStateToXml({ state, background });
        if (!xml) {
            throw new Error('Failed to generate scene XML');
        }
        const region = yield* select(getRegionSetting);

        const uploadId = (yield* call(uploadSceneXml, xml)) as string;

        const initialVersion: SceneType_InitialVersion = {
            indexUri: generateUploadsUrl(region, uploadId),
        };
        const cylinders: CylinderType[] = yield* select(getAllCylinderWarps);
        if (cylinders.length) {
            initialVersion.transformations = fromState(cylinders);
        }
        const garmentMarkers: BackgroundGarmentMarkersType[] = yield* select(getAllGarmentMarkers);
        if (garmentMarkers.length) {
            initialVersion.markers = garmentMarkersFromState(garmentMarkers);
        }
        const garmentPlacements: DocumentGarmentPlacementType[] = yield* select(getAllGarmentPlacements);
        if (garmentPlacements.length) {
            initialVersion.garmentPlacements = garmentPlacementsFromState(garmentPlacements);
        }
        const bodyMeasurementOverride = yield* select(getBodyMeasurementOverride);
        if (bodyMeasurementOverride) {
            initialVersion.bodyMeasurementOverride = bodyMeasurementOverrideFromState(bodyMeasurementOverride);
        }

        const sceneData: SceneType_NotSaved = {
            name,
            description,
            notes,
            tags,
            mcpSku: sku || '',
            mcpSkuVersion: `${skuVersion || ''}`,
            attributes: variables,
            initialVersion,
        };

        if (merchantSku) {
            sceneData.mcpSku = merchantSku;
            sceneData.mcpSkuVersion = `${merchantVersion || ''}`;
            sceneData.attributes = merchantVariables;
        }

        return sceneData;
    } catch (error) {
        logError(`Error creating scene version for saving: ${JSON.stringify(background || {})} ${error}`);
        // Everything that uses this has a try/catch around this. Let them handle the error on their own.
        throw error;
    }
}

function* createUsingBackground(background: BackgroundType): Generator<any, CreateSceneResult, any> {
    const scene = yield* call(getScene, background);
    scene.name = background.name || 'Scene'; // We name our bulk scenes after the background file name
    const { tenantId, tenantType } = yield* select(getCoamTenant);
    return yield* call(createSceneOnServer, { tenantType, tenantId, scene });
}

function* createMany(): Generator<any, void, any> {
    const backgrounds = yield* select(getAllBackgrounds);
    try {
        const results: CreateSceneResult[] = [];
        for (const bg of backgrounds) {
            const result = yield* call(createUsingBackground, bg);
            results.push(result);
        }
        yield* put(createBulkScenesSuccess());
        yield* put(push('/'));
    } catch (error) {
        logError(`Failure to bulk create scenes: ${error}`);
        yield* put(createBulkScenesFailed(apiErrorMessage(error)));
    }
}

// Used by both saveAndPublish and saveAsDraft
function* saveSceneVariation(): Generator<any, void, any> {
    const sceneData = yield* select(getSceneData);
    // only save when there is something to save
    if (!sceneData.sku) {
        return;
    }

    const { tenantId, tenantType } = yield* select(getCoamTenant);
    const tenant: TenantType = { id: tenantId, type: tenantType };
    const scene: SceneVariationScene = {
        ...sceneData,
        tenant,
        skuVersion: sceneData.skuVersion,
        attributes: sceneData.attributes || {},
    };

    const sceneVariationData = yield* select(getSceneVariation);
    if (!sceneVariationData) return;

    const documents = yield* select(getAllDocuments);
    const surfaces = yield* select(getSurfaces);
    const subReferences: any[] = [];
    documents.forEach((doc) => {
        const tempSurface = surfaces[doc.page - 1];
        if (!subReferences.includes(tempSurface)) {
            subReferences.push(tempSurface);
        }
    });
    let savedSceneVariation: SaveSceneVariationAssetResult | undefined;

    try {
        // Get Link
        if (sceneVariationData.sceneVariableId) {
            const currentLink: GetSceneVariationLinkResult = yield* call(getSceneVariationLink, {
                variableConfigurationId: sceneVariationData.sceneVariableId,
                sceneId: scene.id,
            });
            if (currentLink && currentLink.variableConfigurationId) {
                // update link if possible
                // create new version for config variation
                const sceneWithTenant: SceneVariationScene = {
                    ...scene,
                    tenant,
                    skuVersion: scene.skuVersion,
                    attributes: scene.attributes || {},
                };
                savedSceneVariation = (yield* call(updateSceneVariation, {
                    link: currentLink,
                    scene: sceneWithTenant,
                    configData: sceneVariationData,
                    subReferences,
                    tenant,
                })) as SaveSceneVariationAssetResult;
            } else {
                // Create Configuration
                savedSceneVariation = yield* call(saveSceneVariationAsset, { scene, configData: sceneVariationData });
                // update link
                yield* call(updateLinkVariation, {
                    assetId: scene.id,
                    linkId: currentLink?.id || '',
                    variableConfigurationId: savedSceneVariation.id,
                    configData: sceneVariationData,
                    tenant,
                    subReferences,
                });
            }
        } else {
            // Create Configuration
            savedSceneVariation = yield* call(saveSceneVariationAsset, { scene, configData: sceneVariationData });
            if (!savedSceneVariation) {
                logError('Failed to save scene Variation');
                throw new Error('Failed to save scene Variation');
            }

            yield* put(updateSceneIds({ sceneVariableId: savedSceneVariation.id }));
            // Create Link
            const linkData = {
                variableConfigurationId: savedSceneVariation.id,
                referenceId: scene.sku,
                referenceIdVersion: Number(scene.skuVersion),
                subReferenceIds: subReferences.map((ref) => (ref && ref.id) || ''),
                attributes: scene.attributes,
                linkPurposes: mapPurposes(sceneVariationData.scenePurposes),
                tags: sceneVariationData.tags || [],
                view: sceneVariationData.view,
                tenant,
            };

            const saveLink = yield* call(createLink, { assetId: scene.id, linkInput: linkData });
            yield* put(updateSceneIds({ sceneLinkId: saveLink.id }));
        }
        // Reload all links from database
        const links: LinkType[] = yield* call(getLinksForAsset, { assetId: scene.id });
        const sceneVariationConfigs = links.filter((link) => !!link.variableConfigurationId);
        yield* put(setLinks({ links: sceneVariationConfigs }));
    } catch (error: any) {
        logError(`Failed to save scene Variation: ${error}`);
        const apiMessage = apiErrorMessage(error);
        if (
            (error.response &&
                error.response.data.message ===
                    'You already have an asset linked with this mcp product and purpose!') ||
            (error.message === 'Request failed with status code 409' && error.config?.url?.indexOf('/links') !== -1)
        ) {
            yield* put(saveLinkVariationFailed(apiMessage));
        } else if (!savedSceneVariation) {
            yield* put(saveVariationFailed(apiMessage));
        }

        if (!sceneVariationData.sceneVariableId && savedSceneVariation) {
            try {
                yield* call(deleteAsset, { assetId: savedSceneVariation.id });
                yield* put(cleanSceneConfiguration());
            } catch (delError) {
                logError(`Failed to delete scene Variation: ${delError}`);
            }
        }
        throw error;
    }
}

function* create(): Generator<any, void, any> {
    const { tenantId, tenantType } = yield* select(getCoamTenant);
    try {
        const scene = yield* call(getScene);
        const result: CreateSceneResult = yield* call(createSceneOnServer, { tenantType, tenantId, scene });
        logInfo(JSON.stringify(result));
        yield* put(
            createSceneSuccess({
                sceneId: result.id,
                latestVersionId: result.latestVersionId || '',
                publishedVersionId: result.publishedVersionId || '',
            }),
        );

        // If we don't catch it, the save doesn't work after this
        try {
            yield* call(saveSceneVariation);
        } catch (er) {
            logError(`Error creating scene variation: ${er}`);
        }
        yield* put(push(`/editor/${tenantType}/${tenantId}/${result.id}`));
        yield* put(createSceneFinished());
    } catch (error) {
        logError(`Error creating scene: ${error}`);
        yield* put(createSceneFailed(apiErrorMessage(error)));
    }
}

// Used by both saveAndPublish and saveAsDraft. Exported (unlike this file's other handlers) so
// its `versionParams` field list — the exact thing a prior bug (markers/garmentPlacements
// silently dropped on every re-save of an EXISTING scene) and this one (bodyMeasurementOverride,
// same shape) both hit — can be tested directly, mirroring how `documents/saga.ts` exports its
// own individual handlers for the same reason.
export function* saveScene({ draft = false }: { draft?: boolean }): Generator<any, SaveResult, any> {
    const { tenantId, tenantType } = yield* select(getCoamTenant);
    const sceneId = yield* select(getSceneId);

    try {
        const scene = yield* call(getScene);
        if (draft) {
            scene.publishedVersionId = yield* select(getPublishedVersionId);
        }
        const { initialVersion, ...sceneData } = scene;

        // Create version with proper type casting. `markers`/`garmentPlacements`/
        // `bodyMeasurementOverride` are recomputed fresh by `getScene()` above every save (not
        // read back from the previous version), same as `transformations` — carrying them over
        // here just wires that existing pattern into the update path too. This object, not
        // `getScene()`'s own `initialVersion`, is what `createSceneVersion` actually receives on
        // every save after the first (a brand-new scene's first `createScene` call reads
        // `initialVersion` directly) — any field added to `initialVersion` without a matching
        // entry here round-trips once, then silently drops on every re-save.
        const versionParams = {
            indexUri: initialVersion.indexUri,
            transformations: initialVersion.transformations,
            markers: initialVersion.markers,
            garmentPlacements: initialVersion.garmentPlacements,
            bodyMeasurementOverride: initialVersion.bodyMeasurementOverride,
        };
        const newVersion = (yield* call(createSceneVersion, {
            sceneId,
            version: versionParams,
        })) as CreateSceneVersionResult;

        // If a publishedVersionId is passed in, we want to preserve that, as this is saving a draft.
        const updatedScene = {
            ...sceneData,
            publishedVersionId: newVersion.id,
            latestVersionId: newVersion.id,
        };
        yield* call(updateScene, { tenantType, tenantId, sceneId, scene: updatedScene as SceneType_NotSaved });
        return {
            sceneId,
            publishedVersionId: updatedScene.publishedVersionId || '',
            latestVersionId: updatedScene.latestVersionId || '',
        };
    } catch (error) {
        logError(`Failed to save scene: ${error}`);
        throw error;
    }
}

function* saveAndPublish(): Generator<any, void, any> {
    try {
        const result = yield* call(saveScene, { draft: false });
        yield* call(saveSceneVariation);
        yield* put(saveAndPublishSceneSuccess(result));
    } catch (error) {
        logError(`Failed to save and publish scene: ${error}`);
        yield* put(saveAndPublishSceneFailed(apiErrorMessage(error)));
    }
}

function* saveAsDraft(): Generator<any, void, any> {
    try {
        const result = yield* call(saveScene, { draft: true });
        yield* call(saveSceneVariation);
        yield* put(saveDraftSuccess(result));
    } catch (error) {
        logError(`Failed to save as draft: ${error}`);
        yield* put(saveDraftFailed(apiErrorMessage(error)));
    }
}

function* publish(): Generator<any, void, any> {
    const { tenantId, tenantType } = yield* select(getCoamTenant);
    const versionId = yield* select(getLatestVersionId);
    const sceneId = yield* select(getSceneId);
    try {
        yield* call(updateScene, { tenantType, tenantId, sceneId, scene: { publishedVersionId: versionId } });
        yield* put(publishSceneSuccess({ sceneId, publishedVersionId: versionId }));
    } catch (error) {
        logError(`Failed to publish scene: ${sceneId} ${versionId} ${error}`);
        yield* put(publishSceneFailed(apiErrorMessage(error)));
    }
}

function* duplicate(): Generator<any, void, any> {
    const { tenantId, tenantType } = yield* select(getCoamTenant);
    const sceneId = yield* select(getSceneId);
    try {
        const scene = yield* call(getScene);
        scene.name += ' COPY';
        const newScene: any = yield* call(createSceneOnServer, { tenantType, tenantId, scene });
        yield* put(duplicateSceneSuccess({ name: scene.name, sceneId: newScene.id }));
    } catch (error) {
        logError(`Error duplicating scene: ${sceneId} ${error}`);
        yield* put(duplicateSceneFailed(apiErrorMessage(error)));
    }
}

export default function* editorSceneSagas(): Generator<any, void, any> {
    yield* all([
        yield* takeEvery(publishScene.type, publish),
        yield* takeEvery(saveAndPublishScene.type, saveAndPublish),
        yield* takeEvery(createScene.type, create),
        yield* takeEvery(createBulkScenes.type, createMany),
        yield* takeEvery(saveDraft.type, saveAsDraft),
        yield* takeEvery(duplicateScene.type, duplicate),
    ]);
}
